import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { AdminHeader, Badge, Panel } from "../ui";
import SmartImage from "../../components/SmartImage";
import { EmptyState, PageLoader } from "../../components/States";
import { EnquiryNotification } from "../EnquiryNotification";

const STATUSES = ["all", "new", "in-progress", "quoted", "closed"];

export default function EnquiriesAdmin() {
  const [params, setParams] = useSearchParams();
  const racexOnly = params.get("event") === "racex";
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    adminApi.get("/admin/enquiries", { params: { status, ...(racexOnly ? { event_id: "racex-townsville-oct-2026" } : {}) } })
      .then((r) => setRows(r.data.enquiries)).catch(() => setError(true));
  }, [status, racexOnly]);

  useEffect(() => { load(); }, [load]);

  async function setStatusFor(id, s) {
    try {
      await adminApi.put(`/admin/enquiries/${id}`, { status: s });
      toast.success(`Marked ${s}`);
      load();
    } catch { toast.error("Could not update this enquiry. Please try again."); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await adminApi.delete(`/admin/enquiries/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete this enquiry. Please try again."); }
  }

  return (
    <div data-testid="admin-enquiries">
      <AdminHeader title="Enquiries" subtitle={rows ? `${rows.length} enquiries` : ""}>
        <div className="flex flex-wrap gap-1">
          <button type="button" className={racexOnly ? "btn-gold px-3 py-1.5 text-xs" : "btn-outline-nq px-3 py-1.5 text-xs"} aria-pressed={racexOnly} onClick={() => setParams(racexOnly ? {} : { event: "racex" })} data-testid="enquiry-filter-racex">RACEX only</button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={status === s ? "btn-gold px-3 py-1.5 text-xs" : "btn-outline-nq px-3 py-1.5 text-xs"}
              data-testid={`enquiry-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </AdminHeader>

      {error ? <div role="alert" className="p-4 text-red-200" data-testid="admin-enquiries-error">Could not load enquiries. <button onClick={load} className="underline" data-testid="admin-enquiries-retry-button">Retry</button></div> : !rows ? <PageLoader /> : rows.length === 0 ? (
        <EmptyState title="No enquiries" message="Enquiries submitted from the site will appear here." />
      ) : (
        <div className="space-y-4">
          {rows.map((e) => (
            <Panel key={e.id}>
              <div className="flex flex-wrap items-start gap-4" data-testid={`admin-enquiry-${e.id}`}>
                {e.ref_image && (
                  <div className="w-20 shrink-0">
                    <SmartImage src={e.ref_image} alt="" ratio="aspect-square" fit="contain" fallbackLabel={e.ref_title} />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={e.status === "new" ? "gold" : e.status === "closed" ? "neutral" : "green"}>{e.status}</Badge>
                    <span className="font-mono text-xs text-[color:var(--nq-stone)]" data-testid={`enquiry-${e.id}-reference`}>{e.reference}</span>
                    <span className="ml-auto text-xs text-[color:var(--nq-stone)]">{formatDateTime(e.created_at)}</span>
                  </div>

                  <p className="mt-2 font-semibold" data-testid={`enquiry-${e.id}-name`}>{e.name}</p>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-[color:var(--nq-stone)]">
                    {e.email && <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-[color:var(--nq-gold)]" data-testid={`enquiry-${e.id}-email-link`}>
                      <Mail className="h-3.5 w-3.5" /> {e.email}
                    </a>}
                    {e.phone && (
                      <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-[color:var(--nq-gold)]" data-testid={`enquiry-${e.id}-phone-link`}>
                        <Phone className="h-3.5 w-3.5" /> {e.phone}
                      </a>
                    )}
                    {e.vehicle && <span>Vehicle: {e.vehicle}</span>}
                    {e.rego && <span>Rego: {e.rego}</span>}
                  </div>

                  {e.ref_title && (
                    <div
                      className="mt-3 rounded-lg border px-3 py-2 text-xs"
                      style={{ borderColor: "rgba(198,162,74,0.3)", background: "rgba(198,162,74,0.05)" }}
                    >
                      <span className="font-semibold gold-text">Enquiring about: </span>
                      {e.ref_title}
                      {e.ref_brand ? ` · ${e.ref_brand}` : ""}
                      {e.ref_category ? ` · ${e.ref_category}` : ""}
                      {e.ref_price ? ` · ${e.ref_price}` : ""}
                      <span className="ml-1 font-mono text-[10px] text-[color:var(--nq-stone)]">
                        ({e.ref_type})
                      </span>
                    </div>
                  )}

                  <p className="mt-3 whitespace-pre-line text-sm text-[color:var(--nq-stone)]" data-testid={`enquiry-${e.id}-message`}>{e.message}</p>
                  <EnquiryNotification enquiry={e} reload={load} />

                  <div className="mt-3 flex flex-wrap gap-2">
                    {["new", "in-progress", "quoted", "closed"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatusFor(e.id, s)}
                        className="btn-outline-nq px-2.5 py-1 text-[11px]"
                        data-testid={`enquiry-${e.id}-set-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                    <button type="button" onClick={() => remove(e.id)} className="ml-auto rounded p-1.5 hover:bg-white/10" data-testid={`enquiry-${e.id}-delete-button`}>
                      <Trash2 className="h-4 w-4" style={{ color: "#e07a72" }} />
                    </button>
                  </div>
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
